/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  Ruler,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const roofingConfig: SolutionTemplateConfig = {
  tradeName: "Roofing",
  tradeSlug: "roofing",
  hero: {
    badge: "Roofing Business Platform",
    title: "Software for roofing contractors",
    titleHighlight: "from measurement to collection.",
    description:
      "What's your actual cost per square including waste? Is your tear-off labor eating your margin? The homeowner is getting three quotes this week — are you the first one on the desk?",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded roofing costs",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for roofing contractors",
    proofPoints: [
      { icon: Wrench, text: "Tear-off, overlay, and new construction" },
      { icon: DollarSign, text: "Square-based estimating with waste factors" },
      { icon: Clock, text: "Be the first proposal on the desk" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running a roofing business without a system.",
    items: [
      {
        icon: Clock,
        title: "The homeowner is getting three quotes",
        description:
          "You know you can do the job. But you're still working up numbers at 10pm while the other roofer already sent a branded proposal from the truck. Speed wins these bids.",
      },
      {
        icon: DollarSign,
        title: "Waste factor changes your whole margin",
        description:
          "10% waste on a simple gable. 15% on a cut-up hip roof. Miss the waste factor and your material cost is wrong on every square. But it's not in your spreadsheet.",
      },
      {
        icon: FileText,
        title: "Tear-off and install are different crews, different costs",
        description:
          "Tear-off labor, dump fees, and install labor are completely separate cost structures. But your estimate lumps them together because your spreadsheet has one labor column.",
      },
    ],
  },
  stats: [
    { value: "Bid faster", label: "First proposal wins" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From roof measurement to final invoice.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Configure shingle lines by manufacturer, underlayment costs, flashing prices, and tear-off vs install labor rates. Build work packages — a tear-off bundle and an install bundle as separate reusable assemblies with waste factors built in.",
        icon: Wrench,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload the roof plan or measure on site. Calculate squares with waste factor (10-15%). Pull in your work packages. Tear-off labor, install labor, and materials all priced separately. Send a branded proposal before you leave the property.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track crew hours for tear-off and install separately, log material deliveries, manage dump fees. Budget vs. actuals — know your cost per square before the job is done.",
        icon: Ruler,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. Compare shingle line profitability. Ask Hank: 'What's my average cost per square on tear-off and reroof jobs this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that understands squares, waste factors, and the tear-off/install split.",
    items: [
      {
        icon: Layers,
        title: "Work packages with waste factors",
        description:
          "Tear-off bundle (labor + dump fees + equipment) and install bundle (shingles + underlayment + flashing + drip edge + labor) as separate reusable assemblies. Waste factor adjusts material quantities automatically.",
        bullets: [
          "Separate tear-off and install bundles",
          "Waste factor built into material quantities",
          "Configurable per roof type",
          "Track usage across bids",
        ],
      },
      {
        icon: Truck,
        title: "Material pricing by manufacturer",
        description:
          "Track shingle costs by manufacturer and line (Owens Corning Duration, GAF Timberline, etc.). Import supplier pricing. When prices change, update once.",
        bullets: [
          "Per-manufacturer, per-line pricing",
          "Import from supplier spreadsheets",
          "Equipment cost tracking (lifts, safety)",
          "Dump fee tracking",
        ],
      },
      {
        icon: Clock,
        title: "Speed to proposal",
        description:
          "The roofer who sends a professional proposal the same day gets the job. CC generates a branded PDF with material specs, labor breakdown, and warranty terms — send it before you leave the property.",
        bullets: [
          "Branded PDF from estimate in one click",
          "Material specs and warranty terms included",
          "Email send with engagement tracking",
          "Client portal for approval",
        ],
      },
      {
        icon: Ruler,
        title: "Roof measurements",
        description:
          "Upload roof plans and measure areas directly on screen. Calculate squares with waste factor. Quantities feed into your estimate through work packages.",
        bullets: [
          "Measure roof areas on plans",
          "Automatic square calculation",
          "Waste factor adjustment",
          "Multi-page plan support",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Waste factor calculated in your head" },
      { icon: X, text: "Tear-off and install in one line item" },
      { icon: X, text: "Proposals that take two days to send" },
      { icon: X, text: "Shingle prices from last month's quote" },
      { icon: X, text: "No idea what your actual cost per square is" },
      { icon: X, text: "Crew hours tracked on a whiteboard" },
    ],
    after: [
      {
        icon: CheckCircle,
        text: "Waste factor built into every material quantity",
      },
      {
        icon: CheckCircle,
        text: "Tear-off and install priced as separate work packages",
      },
      { icon: CheckCircle, text: "Branded proposal sent from the truck" },
      {
        icon: CheckCircle,
        text: "Current supplier pricing in one cost database",
      },
      {
        icon: CheckCircle,
        text: "Budget vs. actuals — cost per square in real time",
      },
      { icon: CheckCircle, text: "Time entries with approval workflow" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your material costs are accurate, your waste factors are built in, and you're the first proposal on the desk.",
    metrics: [
      { value: "First", label: "Professional proposal sent the same day" },
      { value: "Accurate", label: "Waste factors and material costs built in" },
      { value: "Visible", label: "Cost per square on every job" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions roofing contractors ask",
    items: [
      {
        question: "Does it handle waste factor?",
        answer:
          "Yes. Set waste factor per roof type (10% gable, 15% hip). Material quantities automatically adjust. You see the true material cost before sending the proposal.",
      },
      {
        question: "Can I separate tear-off and install costs?",
        answer:
          "Yes. Build separate work packages for tear-off (labor + dump + equipment) and install (shingles + underlayment + flashing + labor). Each has its own labor rates and material costs.",
      },
      {
        question: "How fast can I send a proposal?",
        answer:
          "Measure the roof, pull in your work packages, and generate a branded PDF — all from one screen. Most roofers send proposals the same day they measure.",
      },
      {
        question: "Can I track different shingle lines?",
        answer:
          "Yes. Set up pricing per manufacturer and product line. Track which lines are most profitable based on actual job costs.",
      },
      {
        question: "Can I track crew hours?",
        answer:
          "Yes. Time entries with approval workflow. Track tear-off and install hours separately against the estimate.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, foremen, or office staff. Role-based access.",
      },
    ],
  },
  cta: {
    title: "Be the first proposal on the desk.",
    description:
      "Accurate estimates, waste factors built in, branded proposals in minutes — 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded roofing costs",
      "Cancel anytime",
    ],
  },
};
